import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';
import { Upload, RefreshCw, Download, CheckCircle2, Circle, Layers, RotateCcw,
  Zap, PlugZap, Clock, Radio, Info, History, BookOpen } from 'lucide-react';
import MeasureAuditDrawer from '../lei/MeasureAuditDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  measured: { label: 'Measured', color: '#059669', bg: '#ecfdf5', icon: CheckCircle2 },
  modeled: { label: 'Modeled', color: '#d97706', bg: '#fffbeb', icon: Circle },
  none: { label: 'No data', color: '#94a3b8', bg: '#f8fafc', icon: Circle },
};
const PILLAR_NAMES = { P1: 'Access & Engagement', P2: 'Performance & Outcomes', P3: 'Capability & Readiness', P4: 'Efficiency', P5: 'Business Impact' };
const PROVIDERS = ['Cornerstone LMS', 'Docebo', 'SAP SuccessFactors', 'Workday Learning', 'Absorb LMS'];

const TEMPLATE = `period,measure_id,field,value
2026-03,training_scope,participants,9150
2026-03,training_scope,eligible_population,12000
2026-03,training_scope,total_workforce,12000
2026-03,training_performance,assessment_score_sum,9600
2026-03,training_performance,assessment_participants,120
2026-03,training_performance,participants_passed,102
2026-03,training_performance,mastery_levels_achieved,96
2026-03,training_performance,mastery_levels_possible,120
2026-03,nps,promoters,60
2026-03,nps,detractors,10
2026-03,nps,nps_total,100
`;

export const LeiRawCapturePanel = () => {
  const [cov, setCov] = useState(null);
  const [conf, setConf] = useState({});
  const [connector, setConnector] = useState(null);
  const [history, setHistory] = useState([]);
  const [provider, setProvider] = useState(PROVIDERS[0]);
  const [cadence, setCadence] = useState('weekly');
  const [busy, setBusy] = useState(false);
  const [auditMid, setAuditMid] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [schema, setSchema] = useState([]);
  const [covMap, setCovMap] = useState({});
  const [covRollup, setCovRollup] = useState(null);
  const fileRef = useRef(null);
  const lmsRef = useRef(null);
  const token = secureStorage.getItem('session_token');
  const authHdr = { Authorization: `Bearer ${token}` };
  const jsonHdr = { ...authHdr, 'Content-Type': 'application/json' };

  const loadCoverage = useCallback(async () => {
    try {
      const [rc, rf, rk, rh, rcov] = await Promise.all([
        fetch(`${API_URL}/api/lei-raw/coverage`, { headers: authHdr }),
        fetch(`${API_URL}/api/lei-raw/confidence`, { headers: authHdr }),
        fetch(`${API_URL}/api/lei-raw/connector`, { headers: authHdr }),
        fetch(`${API_URL}/api/lei-raw/connector/history?limit=8`, { headers: authHdr }),
        fetch(`${API_URL}/api/lei-raw/component-coverage`, { headers: authHdr }),
      ]);
      setCov(await rc.json());
      setConf((await rf.json()).confidence || {});
      const ck = await rk.json();
      if (ck.connector) {
        setConnector(ck.connector);
        setProvider(ck.connector.provider || PROVIDERS[0]);
        setCadence(ck.connector.cadence || 'weekly');
      }
      setHistory((await rh.json()).events || []);
      setCovRollup(await rcov.json());
    } catch (e) { toast.error('Could not load coverage'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  const openAudit = (mid) => { setAuditMid(mid); setAuditOpen(true); };

  const openSchema = async () => {
    setSchemaOpen(true);
    try {
      const [rs, rc] = await Promise.all([
        schema.length ? Promise.resolve(null) : fetch(`${API_URL}/api/lei-raw/schema`, { headers: authHdr }),
        fetch(`${API_URL}/api/lei-raw/component-coverage`, { headers: authHdr }),
      ]);
      if (rs) setSchema((await rs.json()).measures || []);
      const cov = (await rc.json()).measures || [];
      const map = {};
      cov.forEach((m) => { map[m.measure_id] = m; });
      setCovMap(map);
    } catch (e) { toast.error('Could not load the field guide'); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('recompute_after', 'true');
      const r = await fetch(`${API_URL}/api/lei-raw/upload-csv`, { method: 'POST', headers: authHdr, body: fd });
      const d = await r.json();
      if (r.ok) {
        const n = d.recompute?.count ?? 0;
        toast.success(`Uploaded ${d.rows_grouped} measure/period rows · recomputed ${n} measure${n === 1 ? '' : 's'}`);
        if (d.errors?.length) toast.warning(`${d.errors.length} row(s) skipped — check measure_id / numeric values`);
        loadCoverage();
      } else { toast.error(d.detail || 'Upload failed'); }
    } catch (err) { toast.error('Upload failed'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const onRecompute = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/lei-raw/recompute`, { method: 'POST', headers: jsonHdr, body: JSON.stringify({}) });
      const d = await r.json();
      if (r.ok) { toast.success(`Recomputed ${d.count} measure${d.count === 1 ? '' : 's'} from raw inputs`); loadCoverage(); }
      else { toast.error(d.detail || 'Recompute failed'); }
    } catch (e) { toast.error('Recompute failed'); }
    finally { setBusy(false); }
  };

  const onImportLms = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append(f.name.replace(/\.csv$/i, ''), f));
      const r = await fetch(`${API_URL}/api/lei-raw/import-lms`, { method: 'POST', headers: authHdr, body: fd });
      const d = await r.json();
      if (r.ok) {
        const n = d.recompute?.count ?? 0;
        toast.success(`Imported ${d.feeds_received?.length || files.length} LMS feed(s) → ${d.raw_fields} raw fields · recomputed ${n} measure${n === 1 ? '' : 's'}`);
        loadCoverage();
      } else { toast.error(d.detail || 'LMS import failed'); }
    } catch (err) { toast.error('LMS import failed'); }
    finally { setBusy(false); if (lmsRef.current) lmsRef.current.value = ''; }
  };

  const post = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/lei-raw/${path}`, { method: 'POST', headers: jsonHdr, body: JSON.stringify(body || {}) });
      const d = await r.json();
      if (r.ok) { toast.success(typeof okMsg === 'function' ? okMsg(d) : okMsg); loadCoverage(); }
      else { toast.error(d.detail || 'Action failed'); }
    } catch (e) { toast.error('Action failed'); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/lei-raw/connector`, { method: 'DELETE', headers: authHdr });
      setConnector(null);
      toast.success('LMS connector disconnected');
      loadCoverage();
    } catch (e) { toast.error('Disconnect failed'); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'lei_raw_capture_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const backfillMeasure = (m) => {
    const cov = covMap[m.measure_id] || {};
    const fields = (cov.missing && cov.missing.length) ? cov.missing : (cov.required || []);
    if (!fields.length) { toast.info('Nothing missing for this measure'); return; }
    const per = cov.period || '2026-03';
    let csv = 'organization_id,period,measure_id,field,value,cohort_id\n';
    fields.forEach((f) => { csv += `,${per},${m.measure_id},${f},,all\n`; });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `backfill_${m.measure_id}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Backfill template for ${m.name} (${fields.length} field${fields.length === 1 ? '' : 's'})`);
  };

  const counts = cov?.counts || { measured: 0, modeled: 0, none: 0 };
  const pct = cov?.measured_pct ?? 0;
  const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d) ? String(iso).slice(0, 16) : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4" data-testid="lei-raw-capture-panel">
      {/* Data-quality onboarding checklist */}
      {covRollup && (
        <Card className="border-l-4 border-emerald-500" data-testid="onboarding-checklist-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Data-quality setup</span>
              <span className="text-sm font-semibold text-emerald-600" data-testid="onboarding-fully-captured">
                {covRollup.fully_captured}/{covRollup.total_measures} measures fully captured
              </span>
            </CardTitle>
            <CardDescription>Capture raw components pillar by pillar — the app computes each index as you go.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(covRollup.pillars || []).map((p) => {
              const pct = Math.round((p.completeness || 0) * 100);
              return (
                <div key={p.pillar} className="flex items-center gap-3" data-testid={`onboarding-pillar-${p.pillar}`}>
                  <span className="text-xs text-slate-600 w-56 truncate">{p.pillar} · {PILLAR_NAMES[p.pillar] || p.pillar}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${pct === 100 ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-400' : 'bg-slate-300'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-slate-500 w-20 text-right">{p.captured}/{p.total} · {pct}%</span>
                </div>
              );
            })}
            <button type="button" onClick={openSchema} className="text-xs text-cyan-700 hover:underline pt-1" data-testid="onboarding-open-guide">
              Open the field guide to see exactly what to capture →
            </button>
          </CardContent>
        </Card>
      )}

      {/* Demo storytelling */}
      <Card className="border-l-4 border-fuchsia-500" data-testid="demo-replay-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Radio className="h-5 w-5 text-fuchsia-600" /> Demo storytelling — Modeled → Measured
          </CardTitle>
          <CardDescription>
            Replay the value story live: <b>Reset to Modeled</b> starts from synthetic benchmarks, then
            <b> Ingest LMS Feed</b> lights up the raw engine and flips every measure to <b>Measured</b>.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={busy} data-testid="demo-reset-modeled-btn"
            onClick={() => post('demo/reset-modeled', {}, (d) => `Reset to Modeled · ${d.modeled_measures}/${d.total_measures} measures`)}
            className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50">
            <RotateCcw className="h-4 w-4" /> Reset to Modeled
          </Button>
          <Button disabled={busy} data-testid="demo-ingest-lms-btn"
            onClick={() => post('demo/ingest-lms', {}, (d) => `Ingested LMS feed · ${d.measured_measures}/${d.total_measures} measured`)}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Zap className="h-4 w-4" /> Ingest LMS Feed → Measured
          </Button>
        </CardContent>
      </Card>

      {/* Simulated LMS connector */}
      <Card className="border-l-4 border-indigo-500" data-testid="lms-connector-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <PlugZap className="h-5 w-5 text-indigo-600" /> LMS Auto-Sync
            {connector && <Badge className="bg-emerald-50 text-emerald-700 border-0" data-testid="connector-status-badge">Connected</Badge>}
          </CardTitle>
          <CardDescription>
            Connect your LMS once and refresh the raw feeds on a schedule instead of manual CSV uploads.
            <span className="text-slate-400"> (Simulated connector for demonstration.)</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-52">
              <label className="text-xs text-slate-500">Provider</label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger data-testid="connector-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <label className="text-xs text-slate-500">Sync cadence</label>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger data-testid="connector-cadence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" disabled={busy} data-testid="connector-save-btn"
              onClick={() => post('connector', { provider, cadence }, 'LMS connector saved').then(loadCoverage)}>
              {connector ? 'Update' : 'Connect'}
            </Button>
            {connector && (
              <>
                <Button disabled={busy} className="gap-2 bg-indigo-600 hover:bg-indigo-700" data-testid="connector-sync-btn"
                  onClick={() => post('connector/sync', {}, (d) => `Synced from ${d.provider} · ${d.measured_measures}/${d.total_measures} measured`)}>
                  <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Sync now
                </Button>
                <Button variant="ghost" disabled={busy} className="text-rose-600 hover:bg-rose-50" data-testid="connector-disconnect-btn" onClick={disconnect}>
                  Disconnect
                </Button>
              </>
            )}
          </div>
          {connector && (
            <div className="flex items-center gap-4 text-xs text-slate-500" data-testid="connector-sync-info">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Last sync: {fmtDate(connector.last_synced_at)}</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Next sync: {fmtDate(connector.next_sync_at)}</span>
            </div>
          )}
          {history.length > 0 && (
            <div className="pt-2 border-t" data-testid="connector-history">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1">
                <History className="h-3 w-3" /> Sync history
              </div>
              <div className="space-y-1">
                {history.map((ev, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-slate-600" data-testid={`connector-history-row-${i}`}>
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-400" />
                      <span className="text-slate-500">{fmtDateTime(ev.synced_at)}</span>
                      <span className="font-medium text-slate-700">{ev.provider}</span>
                      <Badge variant="outline" className="text-[10px] px-1 py-0 border-slate-200 text-slate-400">{ev.triggered_by || 'manual'}</Badge>
                    </span>
                    <span className="text-slate-500">{ev.measures_refreshed} measures refreshed</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw capture + coverage */}
      <Card className="border-l-4 border-cyan-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-5 w-5 text-cyan-600" /> LEI Raw Capture &amp; Recompute
          </CardTitle>
          <CardDescription>
            Upload raw LMS/CSV data points for the 27 LEI measures. The engine computes each measure index in-app and
            materialises it as <b>measured</b> data — progressively replacing the modeled baselines.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-600">Measurement coverage</span>
              <span className="text-sm font-semibold text-emerald-600" data-testid="lei-coverage-pct">{pct}% measured</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden flex">
              <div className="h-full bg-emerald-500" style={{ width: `${(counts.measured / (cov?.total_measures || 27)) * 100}%` }} />
              <div className="h-full bg-amber-400" style={{ width: `${(counts.modeled / (cov?.total_measures || 27)) * 100}%` }} />
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-emerald-600" data-testid="lei-count-measured">● {counts.measured} measured</span>
              <span className="text-amber-600" data-testid="lei-count-modeled">● {counts.modeled} modeled</span>
              <span className="text-slate-400" data-testid="lei-count-none">● {counts.none} no data</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept=".csv" onChange={onUpload} className="hidden" data-testid="lei-raw-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="gap-2" data-testid="lei-raw-upload-btn">
              <Upload className="h-4 w-4" /> Upload raw CSV
            </Button>
            <Button variant="outline" onClick={onRecompute} disabled={busy} className="gap-2" data-testid="lei-raw-recompute-btn">
              <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Recompute LEI
            </Button>
            <input ref={lmsRef} type="file" accept=".csv" multiple onChange={onImportLms} className="hidden" data-testid="lei-lms-file-input" />
            <Button variant="secondary" onClick={() => lmsRef.current?.click()} disabled={busy} className="gap-2" data-testid="lei-lms-import-btn">
              <Upload className="h-4 w-4" /> Import LMS export
            </Button>
            <Button variant="ghost" onClick={downloadTemplate} className="gap-2" data-testid="lei-raw-template-btn">
              <Download className="h-4 w-4" /> Download template
            </Button>
            <Button variant="ghost" onClick={openSchema} className="gap-2" data-testid="lei-raw-field-guide-btn">
              <BookOpen className="h-4 w-4" /> Field guide
            </Button>
          </div>
          <p className="text-xs text-slate-400">Raw CSV columns: <code>period, measure_id, field, value</code> (optional <code>cohort_id</code>). Provide the <b>raw component values</b> (counts, hours, scores) — the app computes each measure locally per its formula. Click any measure below, or open the Field guide, to see the exact components.</p>
        </CardContent>
      </Card>

      {/* Per-pillar coverage with confidence + audit */}
      {cov?.by_pillar && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(cov.by_pillar).sort().map(([pid, list]) => (
            <Card key={pid} data-testid={`lei-coverage-pillar-${pid}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{pid} · {PILLAR_NAMES[pid] || pid}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {list.map((m) => {
                  const meta = STATUS_META[m.status];
                  const Icon = meta.icon;
                  const c = conf[m.measure_id];
                  return (
                    <button key={m.measure_id} type="button" onClick={() => openAudit(m.measure_id)}
                      className="w-full flex items-center justify-between text-xs rounded px-1 py-1 hover:bg-slate-50 text-left"
                      data-testid={`lei-coverage-measure-${m.measure_id}`}>
                      <span className="text-slate-600 truncate pr-2 flex items-center gap-1">
                        <Info className="h-3 w-3 text-slate-300 shrink-0" />{m.name}
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {c && c.source_mode && c.source_mode !== 'raw' && c.source_mode !== 'modeled' && (
                          <span className="rounded bg-rose-50 px-1 py-0.5 text-[10px] text-rose-600"
                            title="Some sub-measures still use the 0-100 fallback — capture raw components to raise confidence"
                            data-testid={`lei-coverage-source-${m.measure_id}`}>{c.source_mode}</span>
                        )}
                        {c && (
                          <span className={c.confidence >= 0.75 ? 'text-emerald-600' : c.confidence >= 0.5 ? 'text-amber-600' : 'text-rose-500'}
                            data-testid={`lei-coverage-confidence-${m.measure_id}`}>±{c.band}</span>
                        )}
                        <Badge style={{ backgroundColor: meta.bg, color: meta.color }} className="border-0 gap-1">
                          <Icon className="h-3 w-3" /> {meta.label}
                        </Badge>
                      </span>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <MeasureAuditDrawer open={auditOpen} onOpenChange={setAuditOpen} measureId={auditMid} />

      <Dialog open={schemaOpen} onOpenChange={setSchemaOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="lei-schema-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-cyan-600" /> Raw component field guide</DialogTitle>
            <DialogDescription>The exact raw data points to capture for each measure. Green = captured, red = missing. The app computes each index locally per the formula shown.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {schema.map((m) => {
              const cov = covMap[m.measure_id];
              const present = new Set(cov?.present || []);
              const pct = cov ? Math.round((cov.completeness || 0) * 100) : null;
              return (
              <div key={m.measure_id} className="rounded-lg border p-3" data-testid={`schema-measure-${m.measure_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-800">{m.pillar} · {m.name} <code className="text-[11px] text-slate-400">{m.measure_id}</code></div>
                  {pct != null && (
                    <span className="flex items-center gap-2">
                      <span className={`text-[11px] font-medium ${pct === 100 ? 'text-emerald-600' : pct > 0 ? 'text-amber-600' : 'text-rose-500'}`}
                        data-testid={`schema-completeness-${m.measure_id}`}>{pct}% captured</span>
                      {cov && cov.missing && cov.missing.length > 0 && (
                        <button type="button" onClick={() => backfillMeasure(m)}
                          className="text-[11px] text-cyan-700 hover:underline flex items-center gap-0.5"
                          data-testid={`schema-backfill-${m.measure_id}`}>
                          <Download className="h-3 w-3" /> Backfill
                        </button>
                      )}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {m.sub_measures.map((s, i) => (
                    <div key={i} className="text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-700">{s.label || s.op}</span>
                        {s.weight != null && <span className="text-[10px] text-slate-400">weight {s.weight}</span>}
                        <code className="text-[11px] text-cyan-700">{s.formula}</code>
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {s.components.map((c) => {
                          const has = cov ? present.has(c) : null;
                          const cls = has === null ? 'bg-slate-100 text-slate-600'
                            : has ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600';
                          return (
                            <span key={c} className={`rounded px-1.5 py-0.5 text-[11px] ${cls}`}
                              title={has === null ? '' : has ? 'Captured' : 'Missing — backfill this component'}>
                              {has === true ? '✓ ' : has === false ? '• ' : ''}{c}
                            </span>
                          );
                        })}
                        {s.fallback && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">fallback: {s.fallback}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );})}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeiRawCapturePanel;
